import React, { useState, useEffect } from 'react';
import {
  getWebinars,
  createWebinar,
  updateWebinar,
  deleteWebinar
} from '../../services/api';
import {
  Video,
  Plus,
  Edit2,
  Trash2,
  Calendar,
  Clock,
  Users,
  X,
  AlertCircle
} from 'lucide-react';
import './AdminWebinars.css';

const AdminWebinars = () => {
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingWebinar, setEditingWebinar] = useState(null);
  const [deletingWebinar, setDeletingWebinar] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtubeUrl: '',
    scheduledAt: '',
    durationMinutes: 60,
    instructorName: '',
    thumbnailUrl: '',
    category: 'Robot TradingView Copier',
    status: 'scheduled'
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchWebinars();
  }, []);

  const fetchWebinars = async () => {
    try {
      setLoading(true);
      const response = await getWebinars({ limit: 100, sort: '-scheduledAt' });
      setWebinars(response.data.data.webinars || []);
    } catch (error) {
      console.error('Error fetching webinars:', error);
      showNotification('Error loading webinars', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  const handleOpenModal = (webinar = null) => {
    if (webinar) {
      // Edit mode
      setEditingWebinar(webinar);
      const scheduledDate = new Date(webinar.scheduled_at);
      const localDateTime = new Date(scheduledDate.getTime() - scheduledDate.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);

      setFormData({
        title: webinar.title || '',
        description: webinar.description || '',
        youtubeUrl: webinar.youtube_url || '',
        scheduledAt: localDateTime,
        durationMinutes: webinar.duration_minutes || 60,
        instructorName: webinar.instructor_name || '',
        thumbnailUrl: webinar.thumbnail_url || '',
        category: webinar.category || 'Robot TradingView Copier',
        status: webinar.status || 'scheduled'
      });
    } else {
      // Create mode
      setEditingWebinar(null);
      setFormData({
        title: '',
        description: '',
        youtubeUrl: '',
        scheduledAt: '',
        durationMinutes: 60,
        instructorName: '',
        thumbnailUrl: '',
        category: 'Robot TradingView Copier',
        status: 'scheduled'
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingWebinar(null);
    setFormData({
      title: '',
      description: '',
      youtubeUrl: '',
      scheduledAt: '',
      durationMinutes: 60,
      instructorName: '',
      thumbnailUrl: '',
      category: 'Robot TradingView Copier',
      status: 'scheduled'
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) errors.title = 'Title is required';
    // ✅ Only required if NOT live
    if (formData.status !== 'live' && !formData.scheduledAt) {
      errors.scheduledAt = 'Date and time is required';
    }
    if (formData.durationMinutes < 1) errors.durationMinutes = 'Duration must be at least 1 minute';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showNotification('Please fix the form errors', 'error');
      return;
    }

    try {
      // Fix timezone issue: Convert local input time to UTC ISO string
      let scheduledAtISO = formData.scheduledAt;
      if (formData.scheduledAt && formData.status !== 'live') {
        // Robustly parse the datetime-local input (YYYY-MM-DDTHH:mm)
        // We split manually to ensure we create a Date object in the USER'S Local Time
        // new Date("string") can be inconsistent across browsers/formats
        try {
          if (formData.scheduledAt.includes('T')) {
            const [datePart, timePart] = formData.scheduledAt.split('T');
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes] = timePart.split(':').map(Number);

            // new Date(year, monthIndex, ...) is always Local Time
            const localDate = new Date(year, month - 1, day, hours, minutes);

            if (!isNaN(localDate.getTime())) {
              scheduledAtISO = localDate.toISOString();
            }
          } else {
            // Fallback for unexpected formats
            const localDate = new Date(formData.scheduledAt);
            if (!isNaN(localDate.getTime())) {
              scheduledAtISO = localDate.toISOString();
            }
          }
        } catch (err) {
          console.error("Date parsing error:", err);
          // Fallback to original
          const localDate = new Date(formData.scheduledAt);
          if (!isNaN(localDate.getTime())) {
            scheduledAtISO = localDate.toISOString();
          }
        }
      }

      const payload = {
        ...formData,
        durationMinutes: parseInt(formData.durationMinutes),
        //Auto set current time if live and no schedule
        scheduledAt: formData.status === 'live' && !formData.scheduledAt
          ? new Date().toISOString()
          : scheduledAtISO
      };

      if (editingWebinar) {
        await updateWebinar(editingWebinar.id, payload);
        showNotification('Webinar updated successfully!');
      } else {
        await createWebinar(payload);
        showNotification('Webinar created successfully!');
      }

      handleCloseModal();
      fetchWebinars();
    } catch (error) {
      console.error('Error saving webinar:', error);
      showNotification(
        error.response?.data?.message || 'Error saving webinar',
        'error'
      );
    }
  };

  const handleDelete = async () => {
    if (!deletingWebinar) return;

    try {
      await deleteWebinar(deletingWebinar.id);
      showNotification('Webinar deleted successfully!');
      setShowDeleteConfirm(false);
      setDeletingWebinar(null);
      fetchWebinars();
    } catch (error) {
      console.error('Error deleting webinar:', error);
      showNotification(
        error.response?.data?.message || 'Error deleting webinar',
        'error'
      );
    }
  };

  const openDeleteConfirm = (webinar) => {
    setDeletingWebinar(webinar);
    setShowDeleteConfirm(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'live':
        return 'status-live';
      case 'scheduled':
        return 'status-scheduled';
      case 'ended':
        return 'status-ended';
      default:
        return 'status-scheduled';
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-webinars">
      {/* Notification */}
      {notification.show && (
        <div className={`notification notification-${notification.type}`}>
          <AlertCircle size={20} />
          <span>{notification.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="admin-webinars-header">
        <div>
          <h1>Manage Live Webinars</h1>
          <p>Create and manage live streaming sessions</p>
        </div>
        <button className="btn-create" onClick={() => handleOpenModal()}>
          <Plus size={20} />
          Create New Webinar
        </button>
      </div>

      {/* Webinars List */}
      <div className="webinars-list">
        {webinars.length === 0 ? (
          <div className="empty-state">
            <Video size={64} strokeWidth={1.5} />
            <h3>No webinars yet</h3>
            <p>Create your first webinar to get started</p>
          </div>
        ) : (
          <div className="webinars-table">
            <table>
              <thead>
                <tr>
                  <th>Thumbnail</th>
                  <th>Title</th>
                  <th>Date & Time</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Registrations</th>
                  <th>Category</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {webinars.map((webinar) => (
                  <tr key={webinar.id}>
                    <td>
                      <div className="thumbnail-cell">
                        {webinar.thumbnail_url ? (
                          <img src={webinar.thumbnail_url} alt={webinar.title} />
                        ) : (
                          <div className="thumbnail-placeholder">
                            <Video size={20} />
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="title-cell">
                        <strong>{webinar.title}</strong>
                        <span className="instructor-name">{webinar.instructor_name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="date-cell">
                        <Calendar size={16} />
                        {formatDate(webinar.scheduled_at)}
                      </div>
                    </td>
                    <td>
                      <div className="duration-cell">
                        <Clock size={16} />
                        {webinar.duration_minutes} min
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(webinar.status)}`}>
                        {webinar.status}
                      </span>
                    </td>
                    <td>
                      <div className="registrations-cell">
                        <Users size={16} />
                        {webinar.enrolled_count}
                      </div>
                    </td>
                    <td>
                      <span className="category-tag">{webinar.category}</span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleOpenModal(webinar)}
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => openDeleteConfirm(webinar)}
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingWebinar ? 'Edit Webinar' : 'Create New Webinar'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>
                <X size={24} />
              </button>
            </div>

            <form id="webinar-form" onSubmit={handleSubmit} className="webinar-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className={formErrors.title ? 'error' : ''}
                    placeholder="e.g., Live Trading Session: Market Analysis"
                  />
                  {formErrors.title && <span className="error-text">{formErrors.title}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="4"
                    placeholder="Describe what students will learn in this webinar..."
                  />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>YouTube URL</label>
                  <input
                    type="text"
                    value={formData.youtubeUrl}
                    onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                  <span className="help-text">Paste full YouTube link or video ID</span>
                </div>

                <div className="form-group">
                  <label>Thumbnail URL</label>
                  <input
                    type="text"
                    value={formData.thumbnailUrl}
                    onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Scheduled Date & Time *</label>
                  <input
                    type="datetime-local"
                    value={formData.scheduledAt}
                    onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                    className={formErrors.scheduledAt ? 'error' : ''}
                  />
                  {formErrors.scheduledAt && <span className="error-text">{formErrors.scheduledAt}</span>}
                </div>

                <div className="form-group">
                  <label>Duration (minutes) *</label>
                  <input
                    type="number"
                    value={formData.durationMinutes}
                    onChange={(e) => setFormData({ ...formData, durationMinutes: e.target.value })}
                    min="1"
                    max="480"
                    className={formErrors.durationMinutes ? 'error' : ''}
                  />
                  {formErrors.durationMinutes && <span className="error-text">{formErrors.durationMinutes}</span>}
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Instructor Name</label>
                  <input
                    type="text"
                    value={formData.instructorName}
                    onChange={(e) => setFormData({ ...formData, instructorName: e.target.value })}
                    placeholder="Instructor's name"
                  />
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={formData.category === 'Robot TradingView Copier' ? 'Robot TradingView Copier' : 'System'}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'Robot TradingView Copier') {
                        setFormData({ ...formData, category: 'Robot TradingView Copier' });
                      } else {
                        // If switching to System, reset to just "System " or keep existing if already has it?
                        // Resetting is safer to avoid "System Robot..."
                        setFormData({ ...formData, category: 'System ' });
                      }
                    }}
                  >
                    <option value="Robot TradingView Copier">Robot TradingView Copier</option>
                    <option value="System">System</option>
                  </select>

                  {/* Show input if System is selected (or implied) */}
                  {formData.category !== 'Robot TradingView Copier' && (
                    <div style={{ marginTop: '10px' }}>
                      <label style={{ fontSize: '0.85rem', color: '#666' }}>System Name</label>
                      <input
                        type="text"
                        value={formData.category.startsWith('System ') ? formData.category.replace('System ', '') : ''}
                        onChange={(e) => setFormData({ ...formData, category: `System ${e.target.value}` })}
                        placeholder="e.g. Breakout Strategy"
                        style={{ marginTop: '4px' }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="scheduled">Scheduled</option>
                    <option value="live">Live</option>
                    <option value="ended">Ended</option>
                  </select>
                </div>
              </div>

            </form>

            <div className="form-actions" style={{ borderTop: '1px solid #e9ecef', marginTop: 0, borderRadius: '0 0 12px 12px', background: 'white' }}>
              <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                Cancel
              </button>
              <button type="submit" form="webinar-form" className="btn-save">
                {editingWebinar ? 'Update Webinar' : 'Create Webinar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button className="btn-close" onClick={() => setShowDeleteConfirm(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              <p>Are you sure you want to delete this webinar?</p>
              <p className="delete-warning">
                <strong>{deletingWebinar?.title}</strong>
              </p>
              <p>This action cannot be undone.</p>
            </div>

            <div className="form-actions">
              <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="btn-delete-confirm" onClick={handleDelete}>
                Delete Webinar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWebinars;
